import React, {useEffect, useState} from 'react';
import {Alert, Box, Button, Link, TextField, Typography} from '@mui/material';
import {Link as RouterLink, useLocation} from 'react-router-dom';
import {confirmPasswordReset} from '../services/authService';
import {apiErrorMessage, isNetworkError} from '../utilities/apiError';
import {passwordProblem, tokenFromSearch} from '../utilities/passwordReset';

// The page the emailed link opens: choose a new password. The token is read from the address once and then taken out of it, so it does not
// stay in the browser's history or reach another site in a Referer header.
export default function ResetPasswordPage() {
    const {search} = useLocation();
    const [token] = useState(() => tokenFromSearch(search));
    const [password, setPassword] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => { if (token) window.history.replaceState(null, '', window.location.pathname); }, [token]);

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError('');
        const problem = passwordProblem(password, confirmation);
        if (problem) { setError(problem); return; }

        setBusy(true);
        try {
            await confirmPasswordReset(token, password);
            setDone(true);
        } catch (failure) {
            setError(isNetworkError(failure) ? 'Network error. Please try again later.' : apiErrorMessage(failure, "That didn't work. Try again."));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box component="form" onSubmit={submit} noValidate sx={{display: 'flex', flexDirection: 'column', maxWidth: 400, margin: '48px auto 0', padding: 3, gap: 2,
            border: '1px solid', borderColor: 'divider', borderLeft: '4px solid', borderLeftColor: 'primary.main', borderRadius: 2, bgcolor: 'background.paper'}}>
            <Typography variant="h5" component="h1">Choose a new password</Typography>

            {!token && !done && (
                <Alert severity="warning">This page needs the link from the email. <Link component={RouterLink} to="/forgot-password">Ask for a new one.</Link></Alert>
            )}

            {done ? (
                <>
                    <Alert severity="success">Your password is changed. You were signed out everywhere, so sign in with the new one.</Alert>
                    <Button component={RouterLink} to="/login" variant="contained">Sign in</Button>
                </>
            ) : token && (
                <>
                    {error && (
                        <Alert severity="error">
                            {error}{/expired|invalid/i.test(error) && <> <Link component={RouterLink} to="/forgot-password">Ask for a new link.</Link></>}
                        </Alert>
                    )}
                    <TextField label="New password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" autoFocus fullWidth disabled={busy}/>
                    <TextField label="New password again" type="password" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} autoComplete="new-password" fullWidth disabled={busy}/>
                    <Button type="submit" variant="contained" disabled={busy}>{busy ? 'Saving…' : 'Change password'}</Button>
                </>
            )}
        </Box>
    );
}
